import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { User, Mail, Users, LogOut, Crown, Shield, Zap, Palette, Globe, Bell, Bot } from "lucide-react";
import { Trash2, AlertTriangle } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { useUserRole } from "@/hooks/useUserRole";
import { useSubscription, formatDiscountedPrice } from "@/hooks/useSubscription";
import { useThemeColor } from "@/hooks/useThemeColor";
import { sanitizeText } from "@/lib/sanitize";
import { Switch } from "@/components/ui/switch";
import { RefreshCw, CreditCard } from "lucide-react";
import { getCurrentUser } from "@/lib/authUser";

interface Group {
  id: string;
  name: string;
  role: string;
  member_count: number;
}

const Profile = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { role, isTrial, isExpired, trialDaysRemaining, trialEndsAt, canAccessTelegram, loading: roleLoading } = useUserRole();
  const subStatus = useSubscription();
  const { checkSubscription, openCustomerPortal, loading: subLoading, subscribed, productId } = subStatus;
  const { themeColor, updateThemeColor, availableColors } = useThemeColor();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [groups, setGroups] = useState<Group[]>([]);
  const [timezone, setTimezone] = useState("America/New_York");
  const [emailNotificationsEnabled, setEmailNotificationsEnabled] = useState(true);
  const [groupNotifPrefs, setGroupNotifPrefs] = useState<Record<string, boolean>>({});
  const [deletedAt, setDeletedAt] = useState<string | null>(null);
  const [deleteWorking, setDeleteWorking] = useState(false);

  const pathPrefix = "/dashboard";

  useEffect(() => {
    let isMounted = true;
    const loadData = () => { if (isMounted) fetchUserData(); };
    loadData();
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isMounted) loadData();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => { isMounted = false; document.removeEventListener('visibilitychange', handleVisibilityChange); };
  }, []);

  const fetchUserData = async () => {
    const timeoutId = setTimeout(() => {
      setLoading(false);
      toast.error("Loading timeout", { description: "Taking longer than expected. Please refresh." });
    }, 10000);

    try {
      setLoading(true);
      const user = await getCurrentUser();
      if (!user) { clearTimeout(timeoutId); navigate("/login"); return; }
      setEmail(user.email || "");

      const { data: profileData } = await supabase.from("profiles").select("full_name").eq("user_id", user.id).single();
      if (profileData) {
        setDisplayName(profileData.full_name || "");
        setTimezone((profileData as any).timezone || "America/New_York");
        setEmailNotificationsEnabled((profileData as any).email_notifications_enabled ?? true);
        setDeletedAt((profileData as any).deleted_at ?? null);
      }

      const { data: groupsData } = await supabase
        .from("groups")
        .select(`id, name, created_by, group_members!inner(role, user_id)`)
        .order("created_at", { ascending: false });

      if (groupsData) {
        const userGroups = groupsData
          .filter((group: any) => group.group_members.some((m: any) => m.user_id === user.id))
          .map((group: any) => {
            const userMember = group.group_members.find((m: any) => m.user_id === user.id);
            return {
              id: group.id,
              name: group.name,
              role: group.created_by === user.id ? "leader" : userMember?.role || "member",
              member_count: group.group_members.length,
            };
          });
        setGroups(userGroups);

        // Load per-group notification prefs (default true if missing)
        const groupIds = userGroups.map((g: Group) => g.id);
        const prefMap: Record<string, boolean> = {};
        groupIds.forEach((gid: string) => { prefMap[gid] = true; });
        if (groupIds.length > 0) {
          const { data: prefs } = await supabase
            .from("group_notification_prefs" as any)
            .select("group_id, calendar_events_enabled")
            .eq("user_id", user.id)
            .in("group_id", groupIds);
          (prefs ?? []).forEach((p: any) => {
            prefMap[p.group_id] = p.calendar_events_enabled !== false;
          });
        }
        setGroupNotifPrefs(prefMap);
      }
    } catch (error: any) {
      console.error("Error fetching user data:", error);
      toast.error("Error", { description: "Failed to load profile data" });
    } finally {
      clearTimeout(timeoutId);
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const user = await getCurrentUser();
      if (!user) return;
      const sanitizedName = sanitizeText(displayName, 100);
      const { error } = await supabase.from("profiles").update({ full_name: sanitizedName, timezone } as any).eq("user_id", user.id);
      if (error) throw error;
      toast("Profile updated", { description: "Your profile has been updated successfully." });
    } catch (error: any) {
      toast.error("Error", { description: error.message });
    } finally {
      setSaving(false);
    }
  };

  const handleColorChange = async (color: string) => {
    try {
      await updateThemeColor(color);
      toast("Theme updated", { description: "Your theme color has been updated successfully." });
    } catch (error: any) {
      toast.error("Error", { description: "Failed to update theme color" });
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/");
  };

  const handleRequestDeletion = async () => {
    setDeleteWorking(true);
    try {
      const { error } = await supabase.rpc("request_account_deletion" as any);
      if (error) throw error;
      toast("Account deletion scheduled", {
        description: "Your account will be permanently deleted in 14 days. You can cancel anytime before then by logging back in.",
      });
      await supabase.auth.signOut();
      navigate("/");
    } catch (err: any) {
      toast.error("Error", { description: err.message ?? "Failed to schedule deletion" });
    } finally {
      setDeleteWorking(false);
    }
  };

  const handleCancelDeletion = async () => {
    setDeleteWorking(true);
    try {
      const { error } = await supabase.rpc("cancel_account_deletion" as any);
      if (error) throw error;
      setDeletedAt(null);
      toast("Deletion cancelled", { description: "Your account is no longer scheduled for deletion." });
    } catch (err: any) {
      toast.error("Error", { description: err.message ?? "Failed to cancel deletion" });
    } finally {
      setDeleteWorking(false);
    }
  };

  const purgeDate = deletedAt
    ? new Date(new Date(deletedAt).getTime() + 14 * 24 * 60 * 60 * 1000)
    : null;
  const daysRemaining = purgeDate
    ? Math.max(0, Math.ceil((purgeDate.getTime() - Date.now()) / (24 * 60 * 60 * 1000)))
    : 0;

  const getRoleIcon = (role: string) => {
    switch (role) {
      case "leader": return <Crown className="h-4 w-4 text-yellow-500" />;
      case "admin": return <Shield className="h-4 w-4 text-blue-500" />;
      default: return null;
    }
  };

  const getMembershipInfo = () => {
    switch (role) {
      case "trial": return {
        name: "Pro — Free Trial",
        price: trialDaysRemaining !== null
          ? `${trialDaysRemaining} day${trialDaysRemaining === 1 ? "" : "s"} remaining`
          : "7-day free trial",
        color: "text-primary",
        features: [
          "Full Pro access during the trial",
          "Unlimited personal leads",
          "Google Calendar sync",
          "Telegram bot",
          "Your Pro subscription starts when the trial ends",
          "Cancel anytime before then with no charge",
        ],
      };
      case "expired": return {
        name: "Expired",
        price: "Read-only",
        color: "text-destructive",
        features: [
          "Your data is preserved but read-only",
          "Resubscribe to start editing again",
        ],
      };
      case "pro": {
        // Discounted $29 plans: lifetime deal and systeme.io funnel.
        const discounted = productId === "prod_V38uzDFDK50Aqb" || productId === "prod_V6490JGIbZDBG8";
        const promo30 = productId === "prod_V38xr7eEiLf4zo";
        const price = formatDiscountedPrice(subStatus) ?? (discounted ? "$29/month (locked-in rate)" : promo30 ? "$30/month (locked-in rate)" : "$59/month");
        return { name: "Pro", price, color: "text-purple-500", features: ["Unlimited personal leads", "Google Calendar sync", "Telegram bot", "Voice-to-CRM AI", "No group access"] };
      }
      case "business": return { name: "Business", price: formatDiscountedPrice(subStatus, " per user") ?? "$100/month per user", color: "text-amber-500", features: ["Everything in Pro", "Unlimited group workspaces", "Team collaboration", "Priority support"] };
      case "membership": return { name: "Membership", price: "Complimentary", color: "text-amber-500", features: ["Unlimited everything", "Unlimited groups", "Priority support", "Special access"] };
      case "admin": return { name: "Admin", price: "Special", color: "text-red-500", features: ["Full system access", "Administrative privileges"] };
      // legacy fallthrough — should never surface after migration
      case "demo":
      case "standard":
      default: return { name: "Unknown", price: "—", color: "text-muted-foreground", features: [] };
    }
  };

  const membershipInfo = getMembershipInfo();

  if (loading || roleLoading) {
    return (<div className="p-8 flex items-center justify-center"><p className="text-muted-foreground">Loading profile...</p></div>);
  }

  return (
    <div className="p-8 max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Profile</h1>
        <Button variant="destructive" onClick={handleLogout} className="gap-2"><LogOut className="h-4 w-4" />Log Out</Button>
      </div>

      {deletedAt && (
        <Card className="border-destructive bg-destructive/5">
          <CardContent className="pt-6 flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-destructive mt-0.5" />
              <div>
                <p className="font-semibold text-destructive">Account scheduled for deletion</p>
                <p className="text-sm text-muted-foreground">
                  All your data will be permanently erased in {daysRemaining} day{daysRemaining === 1 ? "" : "s"}
                  {purgeDate ? ` (on ${purgeDate.toLocaleDateString()})` : ""}.
                </p>
              </div>
            </div>
            <Button variant="outline" onClick={handleCancelDeletion} disabled={deleteWorking}>
              Cancel deletion
            </Button>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><User className="h-5 w-5" />Personal Information</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="displayName">Display Name</Label>
            <Input id="displayName" value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Enter your name" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-muted-foreground" />
              <Input id="email" value={email} disabled className="bg-muted" />
            </div>
            <p className="text-xs text-muted-foreground">Email cannot be changed</p>
          </div>
          <Button onClick={handleSaveProfile} disabled={saving}>{saving ? "Saving..." : "Save Changes"}</Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><Palette className="h-5 w-5" />Theme Color</CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Label>Choose your theme color</Label>
            <div className="grid grid-cols-4 gap-3">
              {Object.entries(availableColors).map(([key, { hsl, name }]) => (
                <button key={key} onClick={() => handleColorChange(key)} className={`relative p-4 rounded-lg border-2 transition-all ${themeColor === key ? 'border-primary ring-2 ring-primary ring-offset-2' : 'border-border hover:border-primary/50'}`}>
                  <div className="w-full h-12 rounded-md mb-2" style={{ backgroundColor: `hsl(${hsl})` }} />
                  <p className="text-sm font-medium text-center">{name}</p>
                  {themeColor === key && (<div className="absolute top-2 right-2 w-5 h-5 bg-primary rounded-full flex items-center justify-center text-primary-foreground text-xs">✓</div>)}
                </button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><Globe className="h-5 w-5" />Timezone</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="timezone">Your local timezone</Label>
            <Select value={timezone} onValueChange={setTimezone}>
              <SelectTrigger id="timezone"><SelectValue placeholder="Select timezone" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Pacific/Honolulu">Hawaii (HST, UTC-10)</SelectItem>
                <SelectItem value="America/Anchorage">Alaska (AKST, UTC-9)</SelectItem>
                <SelectItem value="America/Los_Angeles">Pacific Time (PST/PDT, UTC-8/-7)</SelectItem>
                <SelectItem value="America/Denver">Mountain Time (MST/MDT, UTC-7/-6)</SelectItem>
                <SelectItem value="America/Phoenix">Arizona (MST, UTC-7)</SelectItem>
                <SelectItem value="America/Chicago">Central Time (CST/CDT, UTC-6/-5)</SelectItem>
                <SelectItem value="America/New_York">Eastern Time (EST/EDT, UTC-5/-4)</SelectItem>
                <SelectItem value="America/Halifax">Atlantic Time (AST/ADT, UTC-4/-3)</SelectItem>
                <SelectItem value="America/Sao_Paulo">Brazil (BRT, UTC-3)</SelectItem>
                <SelectItem value="Europe/London">London (GMT/BST, UTC+0/+1)</SelectItem>
                <SelectItem value="Europe/Paris">Central Europe (CET/CEST, UTC+1/+2)</SelectItem>
                <SelectItem value="Europe/Helsinki">Eastern Europe (EET/EEST, UTC+2/+3)</SelectItem>
                <SelectItem value="Europe/Moscow">Moscow (MSK, UTC+3)</SelectItem>
                <SelectItem value="Asia/Dubai">Dubai (GST, UTC+4)</SelectItem>
                <SelectItem value="Asia/Karachi">Pakistan (PKT, UTC+5)</SelectItem>
                <SelectItem value="Asia/Kolkata">India (IST, UTC+5:30)</SelectItem>
                <SelectItem value="Asia/Dhaka">Bangladesh (BST, UTC+6)</SelectItem>
                <SelectItem value="Asia/Bangkok">Thailand (ICT, UTC+7)</SelectItem>
                <SelectItem value="Asia/Shanghai">China (CST, UTC+8)</SelectItem>
                <SelectItem value="Asia/Singapore">Singapore (SGT, UTC+8)</SelectItem>
                <SelectItem value="Asia/Tokyo">Japan (JST, UTC+9)</SelectItem>
                <SelectItem value="Australia/Sydney">Sydney (AEST/AEDT, UTC+10/+11)</SelectItem>
                <SelectItem value="Pacific/Auckland">New Zealand (NZST/NZDT, UTC+12/+13)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">Used for scheduling timed events in Google Calendar</p>
          </div>
          <Button onClick={handleSaveProfile} disabled={saving}>{saving ? "Saving..." : "Save Timezone"}</Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><Bell className="h-5 w-5" />Notification Preferences</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label htmlFor="email-notifications" className="text-sm font-medium">Weekly Metrics Digest</Label>
              <p className="text-xs text-muted-foreground">Receive a weekly summary of refreshers needing attention, new leads, and key activity metrics.</p>
            </div>
            <Switch
              id="email-notifications"
              checked={emailNotificationsEnabled}
              onCheckedChange={async (checked) => {
                setEmailNotificationsEnabled(checked);
                try {
                  const user = await getCurrentUser();
                  if (!user) return;
                  const { error } = await supabase.from("profiles").update({ email_notifications_enabled: checked } as any).eq("user_id", user.id);
                  if (error) throw error;
                  toast(checked ? "Notifications enabled" : "Notifications disabled", {
                    description: checked ? "You'll receive weekly metric summaries." : "Weekly digest has been turned off.",
                  });
                } catch (error: any) {
                  setEmailNotificationsEnabled(!checked);
                  toast.error("Error", { description: "Failed to update notification preference." });
                }
              }}
            />
          </div>

          {groups.length > 0 && (
            <div className="space-y-3 pt-4 border-t">
              <div className="space-y-1">
                <Label className="text-sm font-medium">Group Calendar Events</Label>
                <p className="text-xs text-muted-foreground">Get an email when anyone adds, updates, or deletes a calendar event in these groups.</p>
              </div>
              {groups.map((g) => (
                <div key={g.id} className="flex items-center justify-between">
                  <div className="flex items-center gap-2 min-w-0">
                    <Users className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span className="text-sm truncate">{g.name}</span>
                  </div>
                  <Switch
                    checked={groupNotifPrefs[g.id] ?? true}
                    onCheckedChange={async (checked) => {
                      const prev = groupNotifPrefs[g.id] ?? true;
                      setGroupNotifPrefs((p) => ({ ...p, [g.id]: checked }));
                      try {
                        const user = await getCurrentUser();
                        if (!user) return;
                        const { error } = await supabase
                          .from("group_notification_prefs" as any)
                          .upsert(
                            { user_id: user.id, group_id: g.id, calendar_events_enabled: checked },
                            { onConflict: "user_id,group_id" }
                          );
                        if (error) throw error;
                        toast(checked ? `Enabled for ${g.name}` : `Disabled for ${g.name}`);
                      } catch (err: any) {
                        setGroupNotifPrefs((p) => ({ ...p, [g.id]: prev }));
                        toast.error("Error", { description: "Failed to update preference." });
                      }
                    }}
                  />
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><Zap className="h-5 w-5" />Membership Tier</CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 border rounded-lg bg-accent/20">
              <div className="space-y-1">
                <p className={`text-2xl font-bold ${membershipInfo.color}`}>{membershipInfo.name}</p>
                <p className="text-sm text-muted-foreground">{membershipInfo.price}</p>
              </div>
              {role !== "admin" && role !== "membership" && (
                <Button variant="outline" size="sm" onClick={() => navigate("/pricing")}>Upgrade</Button>
              )}
            </div>
            {role !== "admin" && role !== "membership" && (
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    await checkSubscription();
                    toast("Subscription refreshed", { description: "Your plan status is up to date." });
                  }}
                  disabled={subLoading}
                  className="gap-2"
                >
                  <RefreshCw className={`h-4 w-4 ${subLoading ? "animate-spin" : ""}`} />
                  {subLoading ? "Refreshing..." : "Refresh subscription"}
                </Button>
                {subscribed && (
                  <Button variant="outline" size="sm" onClick={openCustomerPortal} className="gap-2">
                    <CreditCard className="h-4 w-4" />
                    Manage billing
                  </Button>
                )}
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              Just upgraded and still seeing your old plan? Click refresh to sync with Stripe.
            </p>
            <div className="space-y-2">
              <p className="text-sm font-medium">Features:</p>
              <ul className="space-y-1">
                {membershipInfo.features.map((feature, index) => (
                  <li key={index} className="text-sm text-muted-foreground flex items-center gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-primary" />{feature}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><Users className="h-5 w-5" />Your Groups ({groups.length})</CardTitle></CardHeader>
        <CardContent>
          {groups.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p>You're not part of any groups yet.</p>
              <Button variant="outline" className="mt-4" onClick={() => navigate(`${pathPrefix}/groups`)}>Create or Join a Group</Button>
            </div>
          ) : (
            <div className="space-y-3">
              {groups.map((group) => (
                <div key={group.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent/50 transition-colors">
                  <div className="flex items-center gap-3">
                    {getRoleIcon(group.role)}
                    <div>
                      <p className="font-medium">{group.name}</p>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span>{group.member_count} member{group.member_count !== 1 ? 's' : ''}</span>
                        <span>•</span>
                        <Badge variant="outline" className="text-xs px-1 py-0 h-4">{group.role}</Badge>
                      </div>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => navigate(`${pathPrefix}/groups/${group.id}/hot`)}>View</Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {!deletedAt && (
        <Card className="border-destructive/40">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <Trash2 className="h-5 w-5" />Danger Zone
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="font-medium">Delete account</p>
              <p className="text-sm text-muted-foreground">
                Schedule your account and all associated data (leads, timelines, integrations, history) for permanent deletion.
                You have <strong>14 days</strong> to change your mind by logging back in and clicking "Cancel deletion".
              </p>
            </div>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" disabled={deleteWorking} className="gap-2">
                  <Trash2 className="h-4 w-4" />Delete my account
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete your EchoCRM account?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Your account will be deactivated immediately and permanently deleted after 14 days.
                    During the grace period you can recover everything by signing back in and cancelling.
                    After 14 days, all leads, timeline items, integrations and history are erased and cannot be recovered.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Keep my account</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleRequestDeletion}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    Yes, schedule deletion
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Profile;